import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Drawer-based filter panel. Reads its initial state from filtersValue (the
// server-rendered active filters), wires up Tom Select for multi-select
// fields, and emits the new state by calling the etudes-index controller's
// applyFilters() helper on submit.
export default class extends Controller {
  static targets = [
    "panel", "backdrop",
    "input", "multiSelect", "multiSelectAsync",
    "pillGroup",
    "dateFrom", "dateTo",
    "numberFrom", "numberTo",
    "triState",
    "textSearch"
  ]

  static values = {
    filters:        Object,
    baseUrl:        String,
    membresUrl:     String,
    prestationsUrl: String,
    provenancesUrl: String,
    ownersUrl:      String,
    entreprisesUrl: String
  }

  connect() {
    this.tomSelects = {}
    this._owned = new Set()
    this._initSyncSelects()
    this._initAsyncSelects()
    this._hydrateFromState()
  }

  disconnect() {
    Object.entries(this.tomSelects).forEach(([key, ts]) => {
      if (this._owned.has(key)) ts.destroy()
    })
  }

  open() {
    this.panelTarget.classList.add("is-open")
    this.backdropTarget.classList.add("is-open")
  }

  close() {
    this.panelTarget.classList.remove("is-open")
    this.backdropTarget.classList.remove("is-open")
  }

  pickTriState(event) {
    const tri = event.currentTarget.closest(".tri-state")
    if (!tri) return
    tri.querySelectorAll("button").forEach(b => b.classList.toggle("is-active", b === event.currentTarget))
  }

  togglePill(event) {
    event.currentTarget.classList.toggle("is-active")
  }

  reset() {
    this.inputTargets.forEach(i => { i.value = "" })
    this.dateFromTargets.forEach(i => { i.value = "" })
    this.dateToTargets.forEach(i => { i.value = "" })
    this.numberFromTargets.forEach(i => { i.value = "" })
    this.numberToTargets.forEach(i => { i.value = "" })
    Object.values(this.tomSelects).forEach(ts => ts.clear(true))
    this.triStateTargets.forEach(t => {
      t.querySelectorAll("button").forEach(b => b.classList.toggle("is-active", b.dataset.value === ""))
    })
    this.pillGroupTargets.forEach(g => {
      g.querySelectorAll(".pill-select__option").forEach(b => b.classList.remove("is-active"))
    })
  }

  apply() {
    const filters = this._collectFilters()
    const idx = window.__listIndex || window.__etudesIndex
    if (!idx) {
      console.warn("list-index controller not connected")
      return
    }
    idx.applyFilters(filters)
  }

  // ---- internals ---------------------------------------------------------

  _initSyncSelects() {
    this.multiSelectTargets.forEach(sel => {
      const key = sel.dataset.filterKey
      if (sel.tomselect) {
        this.tomSelects[key] = sel.tomselect
        return
      }
      this.tomSelects[key] = new TomSelect(sel, {
        plugins: ["remove_button"],
        persist: false,
        create: false,
        placeholder: "Sélectionner…"
      })
      this._owned.add(key)
    })
  }

  _initAsyncSelects() {
    this.multiSelectAsyncTargets.forEach(sel => {
      const source = sel.dataset.source
      const url = source === "membres"     ? this.membresUrlValue
                : source === "prestations"  ? this.prestationsUrlValue
                : source === "provenances"  ? this.provenancesUrlValue
                : source === "owners"       ? this.ownersUrlValue
                : source === "entreprises"  ? this.entreprisesUrlValue
                : null
      if (!url) return

      const key = sel.dataset.filterKey
      if (sel.tomselect) {
        sel.tomselect._sourceUrl ||= url
        this.tomSelects[key] = sel.tomselect
        return
      }

      const ts = new TomSelect(sel, {
        valueField: "id",
        labelField: "label",
        searchField: ["label"],
        plugins: ["remove_button"],
        persist: false,
        create: false,
        placeholder: "Rechercher…",
        load: (query, callback) => {
          fetch(`${url}?q=${encodeURIComponent(query || "")}&limit=30`, { headers: { Accept: "application/json" } })
            .then(r => r.json())
            .then(data => callback(data))
            .catch(() => callback())
        }
      })
      this.tomSelects[key] = ts
      this._owned.add(key)

      ts._sourceUrl = url // for hydration
    })
  }

  _hydrateFromState() {
    const filters = this.filtersValue || {}
    Object.entries(filters).forEach(([key, val]) => {
      if (!val || typeof val !== "object") return

      const ts = this.tomSelects[key]
      const inputs = this.element.querySelectorAll(`[data-filter-key="${key}"]`)

      // pill_select hydration
      const pillGroup = this.pillGroupTargets.find(g => g.dataset.filterKey === key)
      if (val.op === "in" && pillGroup) {
        const values = (val.value || []).map(String)
        pillGroup.querySelectorAll(".pill-select__option").forEach(b => {
          b.classList.toggle("is-active", values.includes(b.dataset.value))
        })
        return
      }

      if (val.op === "in" && ts) {
        const values = (val.value || []).map(String)
        if (ts._sourceUrl && values.length) {
          // Pre-load options for currently selected ids
          fetch(`${ts._sourceUrl}?ids[]=${values.join("&ids[]=")}`, { headers: { Accept: "application/json" } })
            .then(r => r.json())
            .then(items => {
              items.forEach(it => ts.addOption(it))
              ts.setValue(values, true)
              ts.refreshOptions(false)
            })
        } else {
          ts.setValue(values, true)
        }
      } else if (val.op === "between") {
        inputs.forEach(input => {
          if (input.dataset.filterBound === "from" && val.from) input.value = val.from
          if (input.dataset.filterBound === "to"   && val.to)   input.value = val.to
        })
      } else if (val.op === "contains") {
        inputs.forEach(input => { if (input.tagName === "INPUT" && input.type === "text") input.value = val.value || "" })
      } else if (val.op === "eq") {
        const tri = this.triStateTargets.find(t => t.dataset.filterKey === key)
        if (tri) {
          tri.querySelectorAll("button").forEach(b => b.classList.toggle("is-active", b.dataset.value === String(val.value)))
        }
      } else if (val.op === "gte") {
        inputs.forEach(input => { if (input.dataset.filterBound === "from") input.value = val.value })
      } else if (val.op === "lte") {
        inputs.forEach(input => { if (input.dataset.filterBound === "to") input.value = val.value })
      }
    })
  }

  _collectFilters() {
    const out = {}

    // text search → contains
    this.inputTargets.forEach(i => {
      const v = i.value.trim()
      if (v) out[i.dataset.filterKey] = { op: "contains", value: v }
    })

    // sync + async multi → in
    Object.entries(this.tomSelects).forEach(([key, ts]) => {
      const values = ts.getValue()
      const arr = Array.isArray(values) ? values : (values ? [values] : [])
      if (arr.length) out[key] = { op: "in", value: arr }
    })

    // pill_select → in
    this.pillGroupTargets.forEach(g => {
      const key = g.dataset.filterKey
      const active = Array.from(g.querySelectorAll(".pill-select__option.is-active"))
                          .map(b => b.dataset.value)
      if (active.length) out[key] = { op: "in", value: active }
    })

    // date_range
    this.dateFromTargets.forEach(input => {
      const key = input.dataset.filterKey
      const to = this.dateToTargets.find(t => t.dataset.filterKey === key)
      const from = input.value
      const toV  = to?.value
      if (from || toV) out[key] = { op: "between", from: from || null, to: toV || null }
    })

    // number_range
    this.numberFromTargets.forEach(input => {
      const key = input.dataset.filterKey
      const to = this.numberToTargets.find(t => t.dataset.filterKey === key)
      const from = input.value
      const toV  = to?.value
      if (from && toV) out[key] = { op: "between", from: from, to: toV }
      else if (from)   out[key] = { op: "gte", value: from }
      else if (toV)    out[key] = { op: "lte", value: toV }
    })

    // tri-state
    this.triStateTargets.forEach(tri => {
      const key = tri.dataset.filterKey
      const active = tri.querySelector("button.is-active")
      if (active && active.dataset.value !== "") {
        out[key] = { op: "eq", value: active.dataset.value === "true" }
      }
    })

    // text_search (utilise par les champs custom text/long_text/email/url/phone)
    if (this.hasTextSearchTarget) {
      this.textSearchTargets.forEach(input => {
        const key = input.dataset.filterKey
        const v   = (input.value || "").trim()
        if (v) out[key] = { op: input.dataset.filterOp || "contains", value: v }
      })
    }

    return out
  }
}
